import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { LevelBadge } from "@/components/LevelBadge";
import { AdminLayout, btn, btnGhost, inp, lbl } from "@/pages/admin/adminShared";
import { LEVELS } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FileUpload } from "@/components/FileUpload";
import { Plus, Pencil, Archive, Search, AlertTriangle, Download } from "lucide-react";

const EMPTY = { name: "", choreographer: "", level: "Beginner", counts: "", walls: "", music: "", artist: "", description: "", tags: "", tutorial_url: "", demo_url: "", step_sheet_url: "", step_sheet_path: "", copperknob_url: "", external_step_sheet_url: "", notes: "", active: true };

function DanceForm({ initial, onSaved, onClose }) {
  const [f, setF] = useState(() => ({ ...EMPTY, ...initial, tags: (initial?.tags || []).join(", ") }));
  const [dupes, setDupes] = useState([]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const editing = !!initial?.id;
  const [ck, setCk] = useState("");
  const [ckText, setCkText] = useState("");
  const [ckLoading, setCkLoading] = useState(false);

  const applyCk = (data) => setF((p) => ({
    ...p,
    name: data.name || p.name, choreographer: data.choreographer || p.choreographer,
    level: data.level || p.level, counts: data.counts ?? p.counts, walls: data.walls ?? p.walls,
    music: data.music || p.music, artist: data.artist || p.artist,
    copperknob_url: data.copperknob_url || p.copperknob_url,
    external_step_sheet_url: data.external_step_sheet_url || p.external_step_sheet_url,
  }));

  const runCk = async (payload) => {
    setCkLoading(true);
    try {
      const { data } = await api.post("/admin/dances/from-copperknob", payload);
      applyCk(data);
      toast.success("Details filled in — please check and save");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not read those details");
    } finally { setCkLoading(false); }
  };

  useEffect(() => {
    if (editing || f.name.trim().length < 3) { setDupes([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/admin/dances/check?name=${encodeURIComponent(f.name)}`); setDupes(data.matches); } catch {}
    }, 350);
    return () => clearTimeout(t);
  }, [f.name, editing]);

  const save = async () => {
    const payload = { ...f, counts: f.counts ? Number(f.counts) : null, walls: f.walls ? Number(f.walls) : null, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/admin/dances/${initial.id}`, payload);
      else await api.post("/admin/dances", payload);
      toast.success(editing ? "Dance updated" : "Dance created");
      onSaved(); onClose();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-[#C85A32]/30 bg-[#FBEBE4]/40 p-3">
        <label className={lbl}>Auto-fill from CopperKnob</label>
        <p className="mb-2 text-xs text-[#6E5B51]">Paste a CopperKnob step sheet link and tap Fetch. If CopperKnob blocks us, open the step sheet, copy the page text and paste it below instead.</p>
        <div className="flex gap-2">
          <input className={inp} value={ck} onChange={(e) => setCk(e.target.value)} placeholder="https://www.copperknob.co.uk/stepsheets/…" data-testid="copperknob-url-input" />
          <button type="button" className={`${btn} shrink-0`} disabled={ckLoading || !ck.trim()} onClick={() => runCk({ url: ck.trim() })} data-testid="copperknob-fetch">{ckLoading ? "…" : "Fetch"}</button>
        </div>
        <textarea className={`${inp} mt-2`} rows={3} value={ckText} onChange={(e) => setCkText(e.target.value)} placeholder="…or paste the step sheet text here (Count, Wall, Level, Choreographer, Music)" data-testid="copperknob-text" />
        <button type="button" className={`${btnGhost} mt-2`} disabled={ckLoading || !ckText.trim()} onClick={() => runCk({ url: ck.trim(), text: ckText })} data-testid="copperknob-fill-text">Fill from pasted text</button>
      </div>
      <div><label className={lbl}>Dance name</label><input className={inp} value={f.name} onChange={set("name")} data-testid="dance-form-name" />
        {dupes.length > 0 && (
          <div className="mt-2 rounded-lg bg-[#FEF3C7] p-2 text-sm text-[#92600A]" data-testid="dupe-warning">
            <p className="flex items-center gap-1 font-semibold"><AlertTriangle size={14} /> Possible existing dance{dupes.length > 1 ? "s" : ""}:</p>
            <p>{dupes.map((d) => d.name).join(", ")} — please reuse if it's the same dance.</p>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={lbl}>Choreographer</label><input className={inp} value={f.choreographer} onChange={set("choreographer")} /></div>
        <div><label className={lbl}>Level</label><select className={inp} value={f.level} onChange={set("level")} data-testid="dance-form-level">{LEVELS.map((l) => <option key={l}>{l}</option>)}</select></div>
        <div><label className={lbl}>Counts</label><input className={inp} type="number" value={f.counts} onChange={set("counts")} /></div>
        <div><label className={lbl}>Walls</label><input className={inp} type="number" value={f.walls} onChange={set("walls")} /></div>
        <div><label className={lbl}>Music</label><input className={inp} value={f.music} onChange={set("music")} /></div>
        <div><label className={lbl}>Artist</label><input className={inp} value={f.artist} onChange={set("artist")} /></div>
      </div>
      <div><label className={lbl}>Description</label><textarea className={inp} rows={2} value={f.description} onChange={set("description")} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={lbl}>Tutorial YouTube URL</label><input className={inp} value={f.tutorial_url} onChange={set("tutorial_url")} /></div>
        <div><label className={lbl}>Demo YouTube URL</label><input className={inp} value={f.demo_url} onChange={set("demo_url")} /></div>
        <div><label className={lbl}>Step sheet URL</label><input className={inp} value={f.step_sheet_url} onChange={set("step_sheet_url")} /></div>
        <div><label className={lbl}>CopperKnob URL</label><input className={inp} value={f.copperknob_url} onChange={set("copperknob_url")} /></div>
      </div>
      <div><label className={lbl}>Tags (comma separated)</label><input className={inp} value={f.tags} onChange={set("tags")} /></div>
      <FileUpload label="Step sheet (PDF upload)" value={f.step_sheet_path} onChange={(path) => setF({ ...f, step_sheet_path: path })} accept=".pdf" kind="file" testid="dance-stepsheet-upload" />
      <DialogFooter>
        <button className={btnGhost} onClick={onClose}>Cancel</button>
        <button className={btn} onClick={save} data-testid="dance-form-save">{editing ? "Save changes" : "Create dance"}</button>
      </DialogFooter>
    </div>
  );
}

export default function ManageDances() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data: dances, isLoading } = useQuery({ queryKey: ["admin-dances"], queryFn: async () => (await api.get("/dances?limit=1000")).data });

  const refetch = () => qc.invalidateQueries({ queryKey: ["admin-dances"] });
  const archive = async (d) => { await api.delete(`/admin/dances/${d.id}`); refetch(); toast("Dance archived"); };
  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setOpen(true); };

  const filtered = (dances || []).filter((d) => !q || d.name.toLowerCase().includes(q.toLowerCase()));

  const [importing, setImporting] = useState(false);
  const doImport = async () => {
    if (!window.confirm("Import the dance library from the old website? Duplicates will be merged into single dances, and each teaching becomes a class posting.")) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/import/wordpress");
      toast.success(`Imported ${data.dances_created} new dances and ${data.assignments_created} class postings`);
      refetch();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Import failed");
    } finally { setImporting(false); }
  };

  return (
    <AdminLayout role={user?.role} title="Dance Library" subtitle="Create each dance once — it's then reusable everywhere."
      actions={<div className="flex gap-2"><button className={btnGhost} onClick={doImport} disabled={importing} data-testid="import-wordpress-button"><Download size={16} /> {importing ? "Importing…" : "Import from old site"}</button><button className={btn} onClick={openNew} data-testid="add-dance-button"><Plus size={16} /> Add dance</button></div>}>
      <div className="mb-4 flex items-center gap-2 rounded-xl border border-border bg-card px-3"><Search size={18} className="text-[#6E5B51]" /><input className="w-full bg-transparent py-2.5 outline-none" placeholder="Search dances" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-dance-search" /></div>
      {isLoading ? <Spinner /> : (
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          {filtered.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-3 border-b border-border p-4 last:border-0" data-testid={`admin-dance-row-${d.slug}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2"><span className="truncate font-semibold text-[#2D1E18]">{d.name}</span><LevelBadge level={d.level} /></div>
                <p className="truncate text-sm text-[#6E5B51]">{d.choreographer}{d.music ? ` · ${d.music}` : ""}</p>
              </div>
              <div className="flex shrink-0 gap-1">
                <button onClick={() => openEdit(d)} data-testid={`edit-dance-${d.slug}`} className="rounded-lg p-2 text-[#5C4A42] hover:bg-secondary" aria-label="Edit"><Pencil size={16} /></button>
                <button onClick={() => archive(d)} data-testid={`archive-dance-${d.slug}`} className="rounded-lg p-2 text-[#6E5B51] hover:bg-secondary hover:text-[#991B1B]" aria-label="Archive"><Archive size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit dance" : "Add a dance"}</DialogTitle></DialogHeader>
          <DanceForm initial={editing} onSaved={refetch} onClose={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
